import { useSession } from '../../session';
import { Button, Panel } from '../../ui';
import type { PanelProps } from '../Event';
import { dateWords } from '../../terms';

/**
 * What the customer actually sees, at the sizes they see it (item 106, D-300).
 *
 * The seat map is judged today by dragging a browser window narrower and
 * squinting. Nobody does that before every change, so the phone view — the one
 * most people use — is the one nobody looks at. A map is the hardest page in the
 * storefront to get right small: it is the one page whose content has a fixed
 * aspect ratio while the controls have to fit around it.
 *
 * **Real windows, not inline frames**, and that is a deliberate refusal rather
 * than a shortcut. The storefront sends `frame-ancestors 'none'` on every page
 * (D-70), and the embed picker changes it to *exactly* the origins an event
 * names — "never to `*`, and never to `'self'` plus a wildcard" (D-116). An
 * inline preview would mean quietly adding this origin to that list for every
 * event: loosening a security header to make a convenience feature prettier. A
 * window opened at the device's width runs the real page, with the real
 * breakpoints, and costs nothing.
 *
 * The widths are outer window widths, so the viewport inside is a few pixels
 * narrower than the device. Said on the panel rather than pretended about: it is
 * enough to catch a layout that breaks at phone width, and not a substitute for
 * a phone.
 */

const SIZES = [
  { label: 'Phone', width: 390, height: 844, note: 'iPhone 15' },
  { label: 'Tablet', width: 820, height: 1180, note: 'iPad Air, portrait' },
  { label: 'Desk', width: 1440, height: 900, note: 'a laptop' },
] as const;

export function PreviewPanel({ detail }: PanelProps) {
  const session = useSession();
  const origin = session.data?.storefront_url?.replace(/\/$/, '') ?? '';
  const performances = detail.performances ?? [];
  const slug = detail.event.slug;

  if (!origin) return null;

  const open = (url: string, size: (typeof SIZES)[number]) => {
    window.open(url, `preview-${size.label}`, `popup=yes,width=${size.width},height=${size.height},noopener`);
  };

  const row = (label: string, detailText: string, url: string) => (
    <div key={url} className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3 last:border-0">
      <span className="min-w-48 text-sm text-ink-900">
        {label}
        <span className="block text-xs text-ink-500">{detailText}</span>
      </span>
      {SIZES.map((size) => (
        <Button key={size.label} onClick={() => open(url, size)}>
          {size.label}
          <span className="ml-1 text-xs font-normal opacity-70">
            {size.width}×{size.height}
          </span>
        </Button>
      ))}
      <Button onClick={() => window.open(url, '_blank', 'noopener')}>Full window</Button>
    </div>
  );

  return (
    <Panel
      title="Preview"
      description="The customer's own pages, opened at the sizes people actually use. Real windows rather than panels on this page, because the storefront refuses to be framed and loosening that for a preview would be the wrong trade."
    >
      {row('The event page', 'What somebody lands on from a link or a search', `${origin}/events/${slug}`)}
      {performances.length === 0 ? (
        <p className="px-5 py-3 text-sm text-ink-500">Add {dateWords().a} and its seat map can be previewed here too.</p>
      ) : (
        performances.slice(0, 6).map((performance) => row('The seat map', String(performance.starts_at ?? ''), `${origin}/events/${slug}/${performance.id}`))
      )}
      <p className="px-5 py-3 text-xs text-ink-500">
        The sizes are the window's, so the page inside is a few pixels narrower than the device. Enough to catch a layout that breaks at phone width; not a substitute for a phone.
      </p>
    </Panel>
  );
}
